import * as THREE from 'three';
import type {Viewer} from '../../viewer';

/**
 * 屏幕空间工具函数模块。
 * 说明：
 * 1. 提供世界坐标到屏幕像素坐标的投影工具；
 * 2. 提供二维点到线段距离计算工具；
 * 3. 提供边缘对象到鼠标屏幕像素距离计算工具；
 * 4. 被多个模块（AbstractPointSnapStrategy、SketchDrawingManager 等）共享使用。
 */

/**
 * 将世界坐标投影到屏幕像素坐标。
 * 说明：
 * 1. 使用 Viewer 的相机和渲染器完成投影；
 * 2. 点在相机背后（NDC z > 1）时返回 null；
 * 3. viewer 未就绪时也返回 null。
 * @param viewer Viewer 实例。
 * @param position 世界坐标。
 * @returns 屏幕像素坐标（原点在左上角）；不可投影时返回 null。
 */
export function projectWorldToScreen(
    viewer: Viewer,
    position: THREE.Vector3,
): THREE.Vector2 | null {
    if (!viewer.camera || !viewer.renderer) {
        return null;
    }
    // 获取渲染器当前尺寸。
    const size = new THREE.Vector2();
    viewer.renderer.getSize(size);

    // 投影到归一化设备坐标（NDC）。
    const ndc = position.clone().project(viewer.camera);

    // 点在相机背后时不可投影。
    if (ndc.z > 1) {
        return null;
    }

    // NDC 转像素坐标（Y 轴翻转：NDC Y+ 对应屏幕上方，像素 Y 向下增大）。
    return new THREE.Vector2(
        (ndc.x * 0.5 + 0.5) * size.x,
        (1 - (ndc.y * 0.5 + 0.5)) * size.y,
    );
}

/**
 * 计算二维点到线段的最短距离。
 * 说明：使用向量投影公式，将点投影到线段所在直线，再夹紧到线段范围内。
 * @param p 待测点。
 * @param a 线段起点。
 * @param b 线段终点。
 * @returns 点到线段的距离。
 */
export function pointToSegmentDistance(
    p: THREE.Vector2,
    a: THREE.Vector2,
    b: THREE.Vector2,
): number {
    const ab = new THREE.Vector2().subVectors(b, a);
    const ap = new THREE.Vector2().subVectors(p, a);
    const abLenSq = ab.lengthSq();

    // 线段退化为点时，直接返回点到点距离。
    if (abLenSq < 1e-10) {
        return ap.length();
    }

    // 计算投影参数 t，夹紧到 [0, 1] 范围。
    const t = Math.max(0, Math.min(1, ap.dot(ab) / abLenSq));

    // 计算投影点并返回距离。
    const proj = a.clone().add(ab.multiplyScalar(t));
    return p.distanceTo(proj);
}

/**
 * 计算鼠标到边缘对象的屏幕像素距离。
 * 说明：
 * 1. 从边缘的 BufferGeometry 中提取所有顶点；
 * 2. 将每对相邻顶点投影到屏幕空间；
 * 3. 计算鼠标点到每条屏幕线段的距离，取最小值。
 * @param viewer Viewer 实例。
 * @param geometry 边缘几何体。
 * @param screenPos 鼠标屏幕坐标。
 * @returns 最短像素距离；无法计算时返回 null。
 */
export function computeEdgeScreenDistance(
    viewer: Viewer,
    geometry: THREE.BufferGeometry,
    screenPos: THREE.Vector2,
): number | null {
    const posAttr = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!posAttr || posAttr.count < 2) {
        return null;
    }

    let minDist = Infinity;

    // 遍历相邻顶点对，计算点到线段的屏幕距离。
    for (let i = 0; i < posAttr.count - 1; i++) {
        const worldA = new THREE.Vector3(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
        const worldB = new THREE.Vector3(posAttr.getX(i + 1), posAttr.getY(i + 1), posAttr.getZ(i + 1));

        const screenA = projectWorldToScreen(viewer, worldA);
        const screenB = projectWorldToScreen(viewer, worldB);
        if (screenA === null || screenB === null) {
            continue;
        }

        const dist = pointToSegmentDistance(screenPos, screenA, screenB);
        if (dist < minDist) {
            minDist = dist;
        }
    }

    return minDist === Infinity ? null : minDist;
}
